import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { eodApi, type EodRunStatus, type ListRunsParams, type EodRunListItemDto } from "@/lib/eod";
import { DataGrid, type DataGridColumn } from "@/components/data-grid";
import { DetailModal } from "@/components/detail-modal";
import { EmptyState } from "@/components/empty-state";
import { ErrorState } from "@/components/error-state";
import { PageHeader } from "@/components/page-header";
import { formatDate, formatDateTime } from "@/lib/format";

const STATUSES: (EodRunStatus | "All")[] = ["All", "Succeeded", "Failed", "Skipped", "PartialSuccess"];

export function EodAuditPage() {
  const [status, setStatus] = useState<EodRunStatus | "All">("All");
  const [page, setPage] = useState(0);
  const [detailId, setDetailId] = useState<number | null>(null);

  const params: ListRunsParams = {
    status: status === "All" ? undefined : status,
    page: page + 1,
    pageSize: 50,
  };

  const runsQuery = useQuery({ queryKey: ["eod-audit", params], queryFn: () => eodApi.listRuns(params) });
  const detailQuery = useQuery({
    queryKey: ["eod-run", detailId],
    queryFn: () => (detailId ? eodApi.getRun(detailId) : Promise.resolve(null)),
    enabled: detailId !== null,
  });

  const rows = runsQuery.data?.items ?? [];

  const columns: DataGridColumn<EodRunListItemDto>[] = [
    {
      id: "date",
      header: "Accounting date",
      accessor: (r) => (
        <button className="underline text-primary" onClick={() => setDetailId(r.eodRunId)}>
          {formatDate(r.accountingDate)}
        </button>
      ),
    },
    { id: "started", header: "Run started", accessor: (r) => formatDateTime(r.runStartedUtc) },
    { id: "status", header: "Status", accessor: (r) => r.status },
    { id: "triggeredBy", header: "Triggered by", accessor: (r) => r.triggeredBy },
    { id: "rows", header: "Rows", accessor: (r) => r.rowsWritten },
  ];

  return (
    <div className="flex flex-col gap-4 p-4">
      <PageHeader title="EOD Audit" />

      <div className="flex gap-2">
        {STATUSES.map((s) => (
          <button
            key={s}
            className={`rounded-full border px-3 py-1 text-sm ${status === s ? "bg-primary text-primary-foreground" : ""}`}
            onClick={() => { setStatus(s); setPage(0); }}
          >
            {s}
          </button>
        ))}
      </div>

      {runsQuery.isError ? (
        <ErrorState onRetry={() => { void runsQuery.refetch(); }} />
      ) : (
        <DataGrid
          rows={rows}
          columns={columns}
          rowKey={(r) => r.eodRunId}
          totalRows={runsQuery.data?.totalCount ?? 0}
          pageIndex={page}
          pageSize={50}
          loading={runsQuery.isLoading}
          emptyHeadline="No EOD runs in selected range"
          onPageChange={setPage}
        />
      )}

      <DetailModal
        open={detailId !== null}
        onOpenChange={(open) => !open && setDetailId(null)}
        title={detailQuery.data ? `EOD run — ${detailQuery.data.accountingDate}` : "EOD run"}
      >
        {detailQuery.data ? (
          <dl className="grid grid-cols-[160px_1fr] gap-y-2 text-sm">
            <dt className="text-muted-foreground">Status</dt><dd>{detailQuery.data.status}</dd>
            <dt className="text-muted-foreground">Triggered by</dt><dd>{detailQuery.data.triggeredBy}</dd>
            <dt className="text-muted-foreground">Rows written</dt><dd>{detailQuery.data.rowsWritten}</dd>
            <dt className="text-muted-foreground">Correlation</dt>
            <dd className="font-mono text-xs">{detailQuery.data.correlationId ?? "—"}</dd>
            <dt className="text-muted-foreground">Rerun reason</dt><dd>{detailQuery.data.rerunReason ?? "—"}</dd>
            <dt className="text-muted-foreground">Error</dt>
            <dd className="whitespace-pre-wrap text-destructive">{detailQuery.data.errorSummary ?? "—"}</dd>
          </dl>
        ) : (
          <EmptyState headline="Loading…" />
        )}
      </DetailModal>
    </div>
  );
}
